import type { OverviewResponse } from "@/shared/api";
import { isZero } from "@/shared/money/money";
import { reasonText } from "@/shared/text/reasons";
import { sourceIssues, type OverviewSources } from "./sources";

const RISK: Readonly<Record<string, string>> = {
  SAFE: "в лимитах",
  ELEVATED: "повышен",
  CRITICAL: "критический",
};

/** The portfolio risk verdict in words; an unknown verdict as written. */
export function riskText(verdict: string): string {
  return RISK[verdict] ?? verdict;
}

// Operational failures, separate from evidence investigations.
export function attention(overview: OverviewResponse, sources?: OverviewSources): readonly string[] {
  const issues: string[] = [...sourceIssues(sources)];
  if (overview.connection !== "HEALTHY") {
    issues.push("Bybit: соединение требует проверки");
  }
  if (overview.stale) issues.push("Данные счёта устарели");
  if (overview.portfolio_risk !== "SAFE") {
    issues.push(`Портфельный риск: ${riskText(overview.portfolio_risk)}`);
  }
  if (overview.performance.capital?.over_allocated === true) {
    issues.push("Аллокация превышает доступный капитал");
  }
  // Operational holds and unattributed exposure exist outside cycle ownership.
  for (const slot of overview.slots) {
    if (slot.blocked_reason != null) {
      issues.push(`${slot.symbol ?? slot.slot_id}: ${reasonText(slot.blocked_reason)}`);
    }
    if (slot.cycle_id == null && !isZero(slot.position_size)) {
      issues.push(`${slot.symbol ?? slot.slot_id}: ${reasonText("UNATTRIBUTED_EXPOSURE")} · ${slot.position_size}`);
    }
  }
  return issues;
}

export function evidenceInvestigations(overview: OverviewResponse): readonly string[] {
  const issues: string[] = [];
  for (const slot of overview.cycles) {
    // Evidence is a v2 promise. A v1 cycle carries no coverage to be
    // incomplete, and reporting one as a gap would put every legacy slot on
    // the list on the day the contract changed.
    if (slot.evidence?.cycle_contract_version === 2) {
      const coverage = slot.evidence.coverage;
      if (
        coverage.grid !== "complete" ||
        coverage.fills !== "complete" ||
        coverage.trailing !== "complete" ||
        coverage.reason_codes.length > 0
      ) {
        const reasons =
          coverage.reason_codes.length === 0
            ? ["EVIDENCE_INCOMPLETE"]
            : coverage.reason_codes;
        for (const reason of reasons) issues.push(`${slot.symbol ?? slot.slot_id}: ${reasonText(reason)}`);
      }
    }
  }
  return issues;
}
